/**
 * L'indicateur d'étape.
 *
 * ## Pourquoi deux présentations
 *
 * L'ancien indicateur était une grille de six colonnes, libellés compris, à
 * toutes les tailles d'écran. Sur un téléphone — d'où la majorité des fiches
 * sont remplies — « Infos personnelles » et « Données santé » se chevauchaient
 * en caractères de six pixels.
 *
 * Un indicateur de progression a deux rôles : dire où l'on en est, et dire
 * combien il reste. Sur petit écran, une barre et « Étape 2 sur 6 · Santé »
 * font les deux ; les cinq autres libellés n'apportent rien qu'on ne sache
 * déjà. Ils reviennent à partir de `sm:`, où ils tiennent.
 *
 * ## Ce qui reste à compléter
 *
 * Quand une partie a des champs signalés, sa pastille porte leur nombre, en
 * orange, et le petit écran le dit à côté du nom de l'étape : le parent voit
 * où aller sans parcourir toute la fiche (retour d'Éclosion, 24/10/2026).
 */

import { Check } from 'lucide-react';
import clsx from 'clsx';
import { ETAPES, type Etape } from './fiche';

const Etapes = ({
  courante,
  atteintes,
  onAller,
  aCompleter = {},
}: {
  courante: Etape;
  /** Les étapes déjà validées, sur lesquelles on peut revenir. */
  atteintes: Etape[];
  onAller: (e: Etape) => void;
  /** Le nombre de champs encore à compléter, par étape. */
  aCompleter?: Partial<Record<Etape, number>>;
}) => {
  const index = ETAPES.findIndex((e) => e.cle === courante);
  const etape = ETAPES[index];
  const ici = aCompleter[courante] ?? 0;

  return (
    <div className="mb-8">
      {/* Petit écran : une barre, un compteur, un nom. */}
      <div className="sm:hidden">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <p className="font-semibold text-gray-900">
            {etape?.titre}
            {ici > 0 && (
              <span className="ml-2 text-sm font-medium text-amber-700">
                {ici === 1 ? '1 à compléter' : `${ici} à compléter`}
              </span>
            )}
          </p>
          <p className="flex-shrink-0 text-sm text-gray-500">
            {index + 1} / {ETAPES.length}
          </p>
        </div>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={ETAPES.length}
          aria-valuenow={index + 1}
          aria-label={`Étape ${index + 1} sur ${ETAPES.length} : ${etape?.titre}`}
        >
          <div
            className="h-full rounded-full bg-primary-600 transition-all duration-300"
            style={{ width: `${((index + 1) / ETAPES.length) * 100}%` }}
          />
        </div>
      </div>

      {/* À partir de sm : les pastilles, avec leurs libellés. */}
      <ol className="hidden sm:flex sm:items-start">
        {ETAPES.map((e, i) => {
          const faite = atteintes.includes(e.cle) && e.cle !== courante;
          const active = e.cle === courante;
          const accessible = faite || active;
          const manque = aCompleter[e.cle] ?? 0;

          return (
            <li key={e.cle} className="flex flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                {/* Le trait de gauche, sauf pour la première. */}
                <span
                  className={clsx(
                    'h-0.5 flex-1',
                    i === 0 ? 'invisible' : faite || active ? 'bg-primary-300' : 'bg-gray-200',
                  )}
                />
                <button
                  type="button"
                  onClick={() => accessible && onAller(e.cle)}
                  disabled={!accessible}
                  aria-current={active ? 'step' : undefined}
                  aria-label={manque > 0 ? `${e.titre} : ${manque} à compléter` : undefined}
                  className={clsx(
                    'relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                    active && 'border-primary-600 bg-primary-600 text-white',
                    faite && 'border-primary-500 bg-white text-primary-600 hover:bg-primary-50',
                    !accessible && 'cursor-not-allowed border-gray-200 bg-white text-gray-300',
                  )}
                >
                  {faite && manque === 0 ? <Check className="h-4 w-4" /> : i + 1}
                  {manque > 0 && (
                    <span
                      className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold leading-none text-white"
                      aria-hidden="true"
                    >
                      {manque}
                    </span>
                  )}
                </button>
                <span
                  className={clsx(
                    'h-0.5 flex-1',
                    i === ETAPES.length - 1 ? 'invisible' : faite ? 'bg-primary-300' : 'bg-gray-200',
                  )}
                />
              </div>
              <span
                className={clsx(
                  'mt-2 px-1 text-center text-xs leading-tight',
                  active ? 'font-semibold text-primary-700' : faite ? 'text-gray-600' : 'text-gray-400',
                )}
              >
                {e.court}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default Etapes;
